import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  ModalProps,
  Paper,
  Progress,
  Stack,
  Switch,
  Table,
  Text
} from "@mantine/core";
import {
  DispatchNotificationCreateRequest,
  DispatchNotificationCreateResponse,
  DispatchNotificationDecision,
  DispatchNotificationOverrideFlags,
  DispatchNotificationPreviewResponse,
  DispatchNotificationResult,
  dispatchNotificationStatus,
  extractErrorMessage,
  toDispatchNotificationSkipReason,
  toDispatchNotificationStatus,
  useCreateDispatchNotificationMutation,
  useLazyPreviewDispatchNotificationsQuery
} from "@ots-frontend/ots-data";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {FormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";
import {IconCircleCheckFilled, IconCircleMinus, IconCircleX} from "@tabler/icons-react";
import {Tooltip} from "../../tooltip/Tooltip/Tooltip";
import {useAutoCloseOnSuccess} from "../useAutoCloseOnSuccess";

export type DispatchNotifyModalProps = ModalProps & {
  dispatchId: string
}

export const DispatchNotifyModal = (props: DispatchNotifyModalProps) => {
  const {dispatchId, onClose, ...rest} = props;

  const {t} = useTranslation(["common"]);

  const [step, setStep] = useState<"preview" | "result">("preview");
  const [override, setOverride] = useState<DispatchNotificationOverrideFlags>({cooldown: false, quiteHours: true});

  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingSend, setLoadingSend] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const [preview, setPreview] = useState<DispatchNotificationPreviewResponse | undefined>(undefined);
  const [result, setResult] = useState<DispatchNotificationCreateResponse | undefined>(undefined);

  const [triggerPreview] = useLazyPreviewDispatchNotificationsQuery();
  const [createDispatchNotification] = useCreateDispatchNotificationMutation()

  useEffect(() => {
    setStep("preview")
    setLoadingPreview(false)
    setLoadingSend(false)
    setPreview(undefined)
    setResult(undefined)
  }, [dispatchId])

  const _onClose = useCallback(() => {
    onClose()
    setStep("preview")
    setLoadingPreview(false)
    setLoadingSend(false)
    setPreview(undefined)
    setResult(undefined)
  }, [onClose])

  const runPreview = useCallback(() => {
    setLoadingPreview(true);
    setError(undefined);

    triggerPreview({dispatchIds: [dispatchId]})
      .unwrap()
      .then((payload) => {
        setPreview(payload);
      })
      .catch((err) => {
        setError(extractErrorMessage(err));
      })
      .finally(() => {
        setLoadingPreview(false);
      });

  }, [triggerPreview, dispatchId])

  const send = useCallback(() => {
    setLoadingSend(true);
    setError(undefined);

    const request: DispatchNotificationCreateRequest = {
      overrideCooldown: override.cooldown,
      overrideQuiteHours: override.quiteHours,
    }

    createDispatchNotification({id: dispatchId, req: request})
      .unwrap()
      .then((payload) => {
        setResult(payload);
        setStep("result");
      })
      .catch((err) => {
        setError(extractErrorMessage(err));
      })
      .finally(() => {
        setLoadingSend(false);
      });

  }, [createDispatchNotification, override, dispatchId])

  useEffect(() => {
    if (!preview && step === 'preview') {
      runPreview()
    }
  }, [dispatchId, step, runPreview, preview])

  const singlePreview = preview?.results?.[0];
  const decision = singlePreview?.decision;

  const queued = result?.queued ?? 0;
  const skipped = result?.skipped ?? 0;
  const failed = result?.failed ?? 0;

  const autoCloseEnabled = queued > 0 && skipped === 0 && failed === 0;

  const { remainingMs, progress } = useAutoCloseOnSuccess({
    enabled: autoCloseEnabled,
    seconds: 4,
    onClose: _onClose,
  });

  return (
    <Modal {...rest} onClose={_onClose}>
      <Stack spacing="md">
        {error && (
          <Alert title={t("common.error.title", "Error")} color="red" mb="md">
            {error}
          </Alert>
        )}

        <Text size="sm" color="dimmed">
          Send notifications to drivers for the selected dispatches.
          Each notification will be evaluated against system safeguards such as cooldowns, quiet hours, and rate limits.
          Some notifications may be skipped if they do not meet delivery conditions.
        </Text>

        {step === "preview" && (
          <>
            <Group position="apart">
              <Text fw={600}>
                {t("common.entity.dispatchnotification.modal.preview", "Preview")}
              </Text>
              <Group spacing="xs">
                <Button variant="subtle" onClick={runPreview} disabled={loadingPreview}>
                  {t("common.action.refresh", "Refresh")}
                </Button>
              </Group>
            </Group>

            <DispatchNotificationOverridesPanel value={override} onChange={setOverride}/>

            {loadingPreview ? (
              <Group position="center" py="md"><Loader/></Group>
            ) : (
              <PreviewTable decisions={decision ? [decision] : []}/>
            )}

            <Group position="apart" mt="sm">
              <Button variant="default" onClick={_onClose}>
                {t("common.action.cancel", "Cancel")}
              </Button>
              <Button onClick={send} loading={loadingSend}>
                {t("common.action.sendnow", "Send now")}
              </Button>
            </Group>
          </>
        )}

        {step === "result" && (
          <>
            <Group position="apart">
              <Text fw={600}>Result</Text>
              <Button variant="subtle" onClick={() => setStep("preview")}>
                {t("common.entity.dispatchnotification.modal.action.backtopreview", "Back to preview")}
              </Button>
            </Group>

            <DispatchNotificationSummaryBar
              queued={result?.queued ?? 0}
              skipped={result?.skipped ?? 0}
              failed={result?.failed ?? 0}
            />

            <ResultsTable results={result?.result ? [result.result] : []}/>

            <Group position="right" mt="sm">
              <Button onClick={_onClose}>
                {t("common.action.done", "Done")}
              </Button>
            </Group>

          </>
        )}

        {autoCloseEnabled && (
          <Group position="apart" mt="sm">
            <Text size="sm" color="dimmed">
              Closing in {Math.ceil(remainingMs / 1000)}s…
            </Text>
            <Progress value={progress} w={180} />
          </Group>
        )}
      </Stack>
    </Modal>
  )
}

export type DispatchNotificationOverridesPanelProps = {
  value: DispatchNotificationOverrideFlags;
  onChange: (v: DispatchNotificationOverrideFlags) => void;
}

export const DispatchNotificationOverridesPanel = (props: DispatchNotificationOverridesPanelProps) => {
  const {value, onChange} = props;

  const {t} = useTranslation(["common"]);

  return (
    <Paper withBorder p="sm">
      <Group position="apart" mb={6}>
        <Text fw={600} size="sm">
          {t('common.entity.dispatchnotification.modal.overrides.title', "Overrides")}
        </Text>
        <Text size="xs" color="dimmed">
          {t('common.entity.dispatchnotification.modal.overrides.desc', "Use sparingly")}
        </Text>
      </Group>

      <Stack spacing={8}>
        <Switch
          checked={value.cooldown}
          onChange={(e) => onChange({...value, cooldown: e.currentTarget.checked})}
          label={t('common.entity.dispatchnotification.modal.overrides.cooldown', "Ignore cooldown")}
        />
        <Switch
          checked={value.quiteHours}
          onChange={(e) => onChange({...value, quiteHours: e.currentTarget.checked})}
          label={t('common.entity.dispatchnotification.modal.overrides.quitehours', "Ignore quite hours")}
        />
      </Stack>
    </Paper>
  );
}

export const DispatchNotificationSummaryBar = ({queued, skipped, failed}: {
  queued: number;
  skipped: number;
  failed: number
}) => {

  const {t} = useTranslation(["common"]);

  return (
    <Group spacing="xs">
      <Badge color="green" variant="light">
        {t("common.entity.dispatchnotification.summary.queued", "Queued")}: {queued}
      </Badge>
      <Badge color="yellow" variant="light">
        {t("common.entity.dispatchnotification.summary.skipped", "Skipped")}: {skipped}
      </Badge>
      <Badge color="red" variant="light">
        {t("common.entity.dispatchnotification.summary.failed", "Failed")}: {failed}
      </Badge>
    </Group>
  );
}

const PreviewTable = ({decisions}: { decisions: DispatchNotificationDecision[] }) => {

  const {t} = useTranslation(["common"]);

  return (
    <Table withBorder highlightOnHover>
      <thead>
      <tr>
        <th>{t("common.entity.dispatchnotification.driver", "Driver")}</th>
        <th>{t("common.entity.dispatchnotification.decision", "Decision")}</th>
        <th>{t("common.entity.dispatchnotification.reason", "Reason")}</th>
        <th>{t("common.entity.dispatchnotification.nextallowed", "Next allowed")}</th>
      </tr>
      </thead>
      <tbody>
      {decisions.length === 0 ? (
        <tr>
          <td colSpan={4}>
            <Text size="sm" color="dimmed">
              {t("common.entity.dispatchnotification.modal.preview.empty", "No previews found.")}
            </Text>
          </td>
        </tr>
      ) : decisions.map((d, idx) => (
        <tr key={idx}>
          <td>
            <Text size="sm">
              {d.driverId ? <DefaultText value={d.driverName}/> : "-"}
            </Text>
          </td>
          <td>
            <DispatchNotificationDecisionBadge decision={d.decision}/>
          </td>
          <td>
            <Text size="sm">
              {d.skipReason ? (
                <DispatchNotificationSkipReason code={d.skipReason}/>
              ) : (
                <>-</>
              )}
            </Text>
          </td>
          <td>
            <Text size="sm" color="dimmed">
              <FormattedDateTime date={d.nextAllowedAtDttm}/>
            </Text>
          </td>
        </tr>
      ))}
      </tbody>
    </Table>
  );
}

const ResultsTable = ({results}: { results: DispatchNotificationResult[] }) => {

  const {t} = useTranslation(["common"]);

  return (
    <Table withBorder highlightOnHover>
      <thead>
      <tr>
        <th>{t("common.entity.dispatchnotification.driver", "Driver")}</th>
        <th>{t("common.entity.dispatchnotification.status", "Status")}</th>
        <th>{t("common.entity.dispatchnotification.reason", "Reason")}</th>
        <th>{t("common.entity.dispatchnotification.notification", "Notification")}</th>
      </tr>
      </thead>
      <tbody>
      {results.length === 0 ? (
        <tr>
          <td colSpan={4}>
            <Text size="sm" color="dimmed">
              {t("common.entity.dispatchnotification.modal.results.empty", "No results.")}
            </Text>
          </td>
        </tr>
      ) : results.map((r, idx) => (
        <tr key={idx}>
          <td>
            <Text size="sm">
              {r.driverId ? <DefaultText value={r.driverName}/> : "-"}
            </Text>
          </td>
          <td>
            {r.status ? (
              <DispatchNotificationStatusBadge code={r.status}/>
            ) : (
              <>-</>
            )}
          </td>
          <td>
            <Text size="sm">
              {r.skipReason ? (
                <DispatchNotificationSkipReason code={r.skipReason}/>
              ) : (
                <>-</>
              )}
            </Text>
          </td>
          <td>
            <DispatchNotificationIcon notificationId={r.notificationId} statusCode={r.status}/>
          </td>
        </tr>
      ))}
      </tbody>
    </Table>
  );
}

export const DispatchNotificationStatusBadge = ({code}: { code: string }) => {

  const {t} = useTranslation(["common"]);

  const status = useMemo(() => {
    return toDispatchNotificationStatus(code)
  }, [code])

  const color = useMemo(() => {
    if (status === dispatchNotificationStatus.queued) {
      return "green"
    }
    if (status === dispatchNotificationStatus.skipped) {
      return "yellow"
    }
    if (status === dispatchNotificationStatus.failed) {
      return "red"
    }
    return "gray"
  }, [status])

  return (
    <Tooltip label={t(status.description.id, status.description.defaultMessage)}>
      <Badge color={color} variant="light">
        <FormattedDatabaseCode value={status}/>
      </Badge>
    </Tooltip>
  )
}

export const DispatchNotificationSkipReason = ({code}: { code: string }) => {

  const {t} = useTranslation(["common"]);

  const skipReason = useMemo(() => {
    return toDispatchNotificationSkipReason(code)
  }, [code])

  return (
    <Tooltip label={t(skipReason.description.id, skipReason.description.defaultMessage)}>
      <FormattedDatabaseCode value={skipReason}/>
    </Tooltip>
  )
}

export const DispatchNotificationDecisionBadge = ({decision}: { decision: string }) => {
  const color = decision === "ALLOW" ? "green" : decision === "SKIP" ? "yellow" : "gray";
  return <Badge color={color} variant="light">{decision}</Badge>;
}

export const DispatchNotificationIcon = ({notificationId, statusCode}: {
  notificationId: string,
  statusCode?: string
}) => {
  const status = useMemo(() => {
    return statusCode ? toDispatchNotificationStatus(statusCode) : undefined
  }, [statusCode])

  if (notificationId) {
    return (
      <Text size="sm" color="green">
        <IconCircleCheckFilled/>
      </Text>
    )
  }

  if (status === dispatchNotificationStatus.skipped) {
    return (
      <Text size="sm" color="yellow">
        <IconCircleMinus/>
      </Text>
    )
  }

  return (
    <Text size="sm" color="red">
      <IconCircleX/>
    </Text>
  )
}
